<template lang="html">
  <v-layout row wrap justify-left>
    <v-flex xs12>
      <span class="title">
        Rate Ingredients
      </span>
    </v-flex>
    <v-flex xs12 v-for="(rate, idx) in tempRates" :key="idx">
      <v-layout v-if="editing === idx">
        <v-flex xs4 class="input-container">
          <v-text-field
            class="pr-4"
            label="Name"
            v-model="tempRates[idx].name"/>
        </v-flex>
        <v-flex xs2 class="input-container">
          <v-text-field
            class="pr-4"
            :label="label(rate.value)"
            type="number"
            v-model="tempRates[idx].value"/>
        </v-flex>
        <v-flex xs4 class="select-container">
          <v-select
            class="pr-4"
            label="Rate Type"
            :items="availableTypes"
            v-model="tempRates[idx].type"/>
        </v-flex>
        <v-flex xs2 class="action-container">
          <save-btn class="save-btn" @click="saveEdit"/>
          <cancel-btn class="cancel-btn" @click="cancelEdit"/>
        </v-flex>
      </v-layout>
      <v-layout row wrap class="content" v-else>
        <v-flex xs4 class="name-container">
          <span class="name"> {{ rate.name }} </span>
        </v-flex>
        <v-flex xs2 class="value-container">
          <span class="value"> {{ valueDisplay(rate.value) }} </span>
        </v-flex>
        <v-flex xs4 class="type-container">
          <span class="type"> {{ typeDisplay(rate.type) }} </span>
        </v-flex>
        <v-flex xs2 class="action-container">
          <edit-btn class="edit-btn" @click="initEdit(idx)"/>
          <confirmation-dialog>
            <template #activator=" { on } ">
              <delete-btn class="delete-btn" v-on="on"/>
            </template>
            <template #main>
              Are you sure you want to delete this Rate Ingredient?
            </template>
            <template #actions=" { deactivate } ">
              <v-btn
                flat
                color="secondary"
                v-on="deactivate">
                I've changed my mind.
              </v-btn>
              <v-btn
                flat
                color="error"
                v-on="deactivate"
                @click="initDelete(idx)">
                Yes, I'm sure.
              </v-btn>
            </template>
          </confirmation-dialog>
        </v-flex>
      </v-layout>
    </v-flex>
    <v-flex xs12 v-if="!!create">
      <v-layout row wrap>
        <v-flex xs4 class="input-container">
          <v-text-field
            class="pr-4"
            label="Name"
            v-model="newRate.name"/>
        </v-flex>
        <v-flex xs2 class="input-container">
          <v-text-field
            class="pr-4"
            :label="label(newRate.type)"
            v-model="newRate.value"/>
        </v-flex>
        <v-flex xs4 class="select-container">
          <v-select
            class="pr-4"
            label="Rate Type"
            :items="availableTypes"
            v-model="newRate.type"/>
        </v-flex>
        <v-flex xs2 class="action-container">
          <save-btn class="save-btn" @click="saveCreate" :disabled="readyToSave"/>
          <cancel-btn class="cancel-btn" @click="cancelCreate"/>
        </v-flex>
      </v-layout>
    </v-flex>
    <v-flex xs12 v-else>
      <add-btn class="add-btn" @click="initCreate"/>
    </v-flex>
  </v-layout>
</template>

<script>
import ConfirmationDialog from './ConfirmationDialog'
import EditButton from './EditButton'
import DeleteButton from './DeleteButton'
import SaveButton from './SaveButton'
import CancelButton from './CancelButton'
import AddButton from './AddButton'

export default {
  components: {
    'edit-btn': EditButton,
    'delete-btn': DeleteButton,
    'save-btn': SaveButton,
    'cancel-btn': CancelButton,
    'add-btn': AddButton,
    'confirmation-dialog': ConfirmationDialog
  },
  props: {
    rates: { type: Array, required: true, default: () => ([ ]) }
  },
  data () {
    return {
      availableTypes: [
        { text: '/mi (live)', value: 'livemiles' },
        { text: '/mi (dead)', value: 'deadmiles' },
        { text: 'Extra Driver', value: 'extradriver' },
        { text: '/hour', value: 'hourly' },
        { text: '/day (No Travel)', value: 'perday' },
        { text: '/night', value: 'pernight' },
        { text: 'Flat (Per Leg)', value: 'flat' },
        { text: 'Flat (Per Trip)', value: 'flattrip' },
        //{ text: '/leg', value: 'perleg' },
        { text: 'Leg Rate', value: 'leg' }
      ],
      editing: null,
      tempRates: [],
      create: false,
      newRate: {}
    }
  },
  mounted () {
    this.newRate = this.freshNewRatePayload()
    this.tempRates = this.rates.map(r=>({name:r.name, type: r.type, value:r.value}))
  },
  computed: {
    creating () {
      return this.rates.length === 0
    },
    typeDisplay () {
      return t => this.availableTypes.find(r => r.value === t).text
    },
    valueDisplay () {
      return v => `$${v}`
    },
    readyToSave () {
      return !this.newRate.name || !this.newRate.type || !this.newRate.value
    }
  },
  watch: {
    rates (val) {
      this.tempRates = val.map(r => ({ name:r.name, type: r.type, value: r.value}))
    }
  },
  methods: {
    label (unused) {
      return '$'
    },
    initCreate () {
      this.create = true
    },
    freshNewRatePayload () {
      return {
        name: null,
        value: null,
        type: null
      }
    },
    saveCreate () {
      this.tempRates.push(this.newRate)
      this.newRate = this.freshNewRatePayload()
      this.saveEdit()
    },
    cancelCreate () {
      this.create = false
      this.newRate = this.freshNewRatePayload()
    },
    initEdit(index) {
      this.editing = index
    },
    initDelete (index) {
      this.tempRates.splice(index, 1)
      this.saveEdit()
    },
    saveEdit () {
      const payload = this.cleanPayload(this.tempRates)
      this.editing = null
      this.$emit('save', payload)
    },
    cancelEdit () {
      this.editing = null
      this.tempRates = this.rates.map(r=>({name:r.name, type: r.type, value:r.value}))
    },
    cleanPayload (p) {
      const temp = p.map(r => ({
        name: r.name,
        type: r.type,
        value: parseFloat(r.value)
      }))
      return temp
    }
  }
}
</script>

<style lang="css" scoped>
.edit-btn {
  margin-top: 0px;
  margin-bottom: 0px;
}
.delete-btn {
  margin-top: 0px;
  margin-bottom: 0px;
}
</style>
